import React from 'react'
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Card,
  CardBody,
  Box,
  VStack,
  Text,
  Spinner,
  Center,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'

const MotionTr = motion(Tr as any)

export interface TableColumn<T> {
  key: keyof T | string
  header: string
  accessor?: (item: T) => React.ReactNode
  hideOnMobile?: boolean
  width?: string
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
}

export interface BaseDataTableProps<T> {
  columns: TableColumn<T>[]
  data: T[]
  loading?: boolean
  emptyMessage?: string
  emptyDescription?: string
  emptyIcon?: React.ReactNode
  onRowClick?: (item: T) => void
  getRowKey?: (item: T, index: number) => string | number
  enableHover?: boolean
  animateRows?: boolean
}

export default function BaseDataTable<T>({
  columns,
  data,
  loading = false,
  emptyMessage = 'No data available',
  emptyDescription,
  emptyIcon,
  onRowClick,
  getRowKey = (item, index) => index,
  enableHover = true,
  animateRows = true,
}: BaseDataTableProps<T>) {
  // Color mode values
  const tableBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const headerBg = useColorModeValue('gray.50', 'gray.750')
  const headerColor = useColorModeValue('gray.700', 'gray.300')
  const hoverBg = useColorModeValue('gray.50', 'gray.700')

  // Filter columns based on responsive settings
  const visibleColumns = columns.filter((column) => {
    if (column.hideOnMobile) {
      return useBreakpointValue({ base: false, md: true })
    }
    return true
  })

  if (loading) {
    return (
      <Card
        bg={tableBg}
        borderRadius="xl"
        boxShadow="sm"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <CardBody>
          <Center py={12}>
            <VStack spacing={4}>
              <Spinner size="lg" color="blue.500" thickness="3px" />
              <Text color="gray.600">Loading...</Text>
            </VStack>
          </Center>
        </CardBody>
      </Card>
    )
  }

  if (data.length === 0) {
    return (
      <Card
        bg={tableBg}
        borderRadius="xl"
        boxShadow="sm"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <CardBody>
          <Center py={12}>
            <VStack spacing={4} textAlign="center">
              {emptyIcon && (
                <Box color="gray.400" fontSize="3xl">
                  {emptyIcon}
                </Box>
              )}
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="medium" color="gray.600">
                  {emptyMessage}
                </Text>
                {emptyDescription && (
                  <Text fontSize="sm" color="gray.500" maxW="md">
                    {emptyDescription}
                  </Text>
                )}
              </VStack>
            </VStack>
          </Center>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card
      bg={tableBg}
      borderRadius="xl"
      boxShadow="sm"
      _hover={{ boxShadow: 'md' }}
      transition="all 0.2s"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
    >
      <CardBody p={0}>
        <TableContainer>
          <Table variant="simple">
            <Thead bg={headerBg}>
              <Tr>
                {visibleColumns.map((column) => (
                  <Th
                    key={String(column.key)}
                    border="none"
                    color={headerColor}
                    fontWeight="semibold"
                    fontSize="sm"
                    textTransform="none"
                    letterSpacing="normal"
                    py={4}
                    px={6}
                    width={column.width}
                    textAlign={column.align || 'left'}
                  >
                    {column.header}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              <AnimatePresence>
                {data.map((item, index) => (
                  <MotionTr
                    key={getRowKey(item, index)}
                    initial={animateRows ? { opacity: 0, y: 10 } : undefined}
                    animate={animateRows ? { opacity: 1, y: 0 } : undefined}
                    exit={animateRows ? { opacity: 0, y: -10 } : undefined}
                    transition={
                      animateRows ? { duration: 0.2, delay: index * 0.02 } : { duration: 0.2 }
                    }
                    cursor={onRowClick ? 'pointer' : 'default'}
                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                    whileHover={
                      enableHover
                        ? {
                            backgroundColor: hoverBg,
                            y: -1,
                          }
                        : {}
                    }
                  >
                    {visibleColumns.map((column) => (
                      <Td
                        key={String(column.key)}
                        border="none"
                        py={4}
                        px={6}
                        textAlign={column.align || 'left'}
                      >
                        {column.accessor
                          ? column.accessor(item)
                          : String((item as any)[column.key] || '')}
                      </Td>
                    ))}
                  </MotionTr>
                ))}
              </AnimatePresence>
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  )
}
